import { useMemo } from 'react'
import { Ban, Pencil, UserRoundSearch } from 'lucide-react'
import { useNavigate } from 'react-router'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'

import { defaultColumnOptions, type TableAny } from '@/utils/tables'
import Flex from '@/elements/Flex'
import Avatar from '@/elements/Avatar'
import Text from '@/elements/Text'
import PopOver from '@/elements/PopOver'
import type { SubjectResponse } from '@/api/types/Subject'
import { getUserLocation } from '@/utils/location'

export interface OrganizationIndividualTableType {
  id: SubjectResponse['id']
  name: SubjectResponse['user']['details']['fullName']
  group: string | undefined
  location: string
  avatarImg?: string
  delegates?: SubjectResponse['user']['delegates']
}

const columnHelper = createColumnHelper<OrganizationIndividualTableType>()

export function useOrganizationIndividualsTable(
  members: SubjectResponse[],
  onDeactivate: (id: string) => void
) {
  const navigate = useNavigate()

  const data: OrganizationIndividualTableType[] = useMemo(() => members.map(row => ({
    id: row.id,
    name: row.user?.details?.fullName,
    group: row.group?.name,
    location: getUserLocation(row.user?.details),
    avatarImg: row.user?.assets?.PROFILE_PICTURE || undefined,
    delegates: row.user?.delegates || []
  })), [members])

  const columns: ColumnDef<OrganizationIndividualTableType, TableAny>[] = [
    columnHelper.accessor('name', {
      ...defaultColumnOptions,
      header: 'Name',
      enableSorting: false,
      cell: info => {
        return (
          <Flex inline align='center' gap={2} className='user-cell'>
            <Avatar
              name={info.getValue() as string}
              src={info.row.original.avatarImg}
              size='extraSmall'
              color='gray'
            />
            <Text variant='textblock'>{info.getValue() as string}</Text>
          </Flex>
        )
      },
    }),
    columnHelper.accessor('group', {
      ...defaultColumnOptions(),
      header: 'Group',
      enableSorting: false,
    }),
    columnHelper.accessor('location', {
      ...defaultColumnOptions(),
      header: 'Location',
      enableSorting: false,
    }),
    columnHelper.accessor('delegates', {
      ...defaultColumnOptions(),
      header: 'Delegates',
      enableSorting: false,
      cell: info => {
        const delegates = info.getValue()
        return (
          <Flex inline stack>
            {delegates?.map(delegate => (
              <Avatar
                key={delegate.id}
                name={delegate.details.fullName}
                src={delegate.assets?.PROFILE_PICTURE || ''}
                size='extraSmall'
              />
            ))}
          </Flex>
        )
      }
    }),
    columnHelper.accessor('id', {
      id: 'actions',
      ...defaultColumnOptions(),
      header: 'Actions',
      meta: { width: 50 },
      enableSorting: false,
      cell: info => {
        const userId = info.row.original.id
        return (
          <Flex justify='center' align='center' className='actions-cell'>
            <PopOver
              position='left'
              options={[
                {
                  label: 'View Profile',
                  action: () => navigate(`/profile/${userId}`),
                  icon: UserRoundSearch
                },
                {
                  label: 'Edit Settings',
                  action: () => navigate(`/individual/${userId}`),
                  icon: Pencil,
                },
                {
                  label: 'Deactivate User',
                  action: () => onDeactivate(userId),
                  icon: Ban,
                  isDanger: true,
                },
              ]}
            />
          </Flex>
        )
      }
    }),
  ]

  return { data, columns }
}
